<template>
	<div>
		<div class="gh-layout">
			<aside class="gh-intro">
				<h2 class="gh-intro__title">Permissions</h2>
				<p class="gh-intro__desc">
					All permissions known to MC2. Roles grant a subset of these; the backend enforces them through
					<code>Gate::allows</code> checks.
				</p>
				<div v-if="isSrdjan" class="mt-3">
					<button class="gh-save-btn" :disabled="scanning" @click="scanPermissions">
						<i class="fas" :class="scanning ? 'fa-spinner fa-spin' : 'fa-search'"></i>
						{{ scanning ? 'Scanning…' : 'Fetch all permissions' }}
					</button>
					<div class="gh-help">
						Scans the mc2-api codebase for permission checks and adds any that are missing from the database.
					</div>
				</div>
			</aside>

			<section class="gh-accounts">
				<div class="ada-card">
					<div v-if="loading" class="text-muted">Loading…</div>
					<template v-else>
						<div class="gh-field mb-3">
							<input
								type="text"
								class="form-control"
								v-model="search"
								placeholder="Search permissions…"
							/>
						</div>
						<div class="perm-scroll">
						<table class="roles-table">
							<thead>
								<tr>
									<th>Name</th>
									<th>Description</th>
									<th>Roles</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="permission in filteredPermissions" :key="permission.id">
									<td class="roles-table__name">{{ permission.name }}</td>
									<td>{{ permission.description || '—' }}</td>
									<td>{{ permission.roles_count }}</td>
								</tr>
							</tbody>
						</table>
						<div v-if="!filteredPermissions.length" class="text-muted">No permissions match “{{ search }}”.</div>
					</div>
					</template>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import restApi from '@/api'
import { showGlobalToast } from '@/utils/toast'

export default {
	name: 'PermissionsSettings',
	data() {
		return {
			permissions: [],
			search: '',
			loading: false,
			scanning: false,
		}
	},
	computed: {
		...mapState(['authUser']),
		isSrdjan() {
			return (this.authUser?.email || '').toLowerCase().includes('srdjan')
		},
		filteredPermissions() {
			const query = this.search.trim().toLowerCase()
			if (!query) return this.permissions
			return this.permissions.filter(
				permission =>
					permission.name.toLowerCase().includes(query) ||
					(permission.description || '').toLowerCase().includes(query)
			)
		},
	},
	created() {
		this.loadPermissions()
	},
	methods: {
		async loadPermissions() {
			this.loading = true
			try {
				const { data } = await restApi.get('/permissions', { params: { full: 1 } })
				this.permissions = Array.isArray(data) ? data : []
			} catch (e) {
				console.error('Failed to load permissions', e)
				showGlobalToast('Failed to load permissions', 'danger')
			} finally {
				this.loading = false
			}
		},
		async scanPermissions() {
			this.scanning = true
			try {
				const { data } = await restApi.post('/permissions/scan')
				this.permissions = data?.permissions || this.permissions
				const created = data?.created || []
				this.$swal.fire({
					icon: 'success',
					title: `Found ${data?.found_in_code ?? 0} permissions in code`,
					text: created.length
						? `Added ${created.length} new: ${created.join(', ')}`
						: 'Nothing new — the database already has them all.',
				})
			} catch (e) {
				showGlobalToast(e?.response?.data?.message || 'Permission scan failed', 'danger')
			} finally {
				this.scanning = false
			}
		},
	},
}
</script>
